/**
 * Broker Management Modal
 *
 * Sleek tabular UI for managing multiple broker connections.
 * - Collapsible sections per prop firm
 * - Radio buttons for global account selection (one active across all)
 * - Add new prop firms dynamically
 */

import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useBrokerStore, BrokerState, BrokerBalance, BrokerPosition, BrokerOrder } from '../store/brokerStore';
import { wsService } from '../services/WebSocketService';
import { useToastStore } from '../store/toastStore';
import { useConnectionStatus } from '../store/selectors';
import { BarChart3, RefreshCw, WifiOff } from 'lucide-react';
import { API_BASE_URL, DEFAULT_BROKER_ID } from '../config/ports';
import { apiFetch } from '../services/api';
import './BrokerManagementModal.css';

// Helper to find TP/SL orders for a position
const findOrdersForPosition = (
  orders: BrokerOrder[],
  position: BrokerPosition
): { tpOrder?: BrokerOrder; slOrder?: BrokerOrder } => {
  const isLong = position.netPos > 0;
  const exitAction = isLong ? 'Sell' : 'Buy';

  // Filter working orders that match the position's exit direction
  const workingOrders = orders.filter(
    (o) =>
      o.accountId === position.accountId &&
      o.action === exitAction &&
      (o.ordStatus === 'Working' || o.ordStatus === 'Accepted' || o.ordStatus === 'Pending')
  );

  // TP is a limit order (exit at profit)
  const tpOrder = workingOrders.find(
    (o) => o.ordType?.toLowerCase().includes('limit') && !o.ordType?.toLowerCase().includes('stop') && o.price
  );

  // SL is a stop order (exit at loss)
  const slOrder = workingOrders.find((o) => o.ordType?.toLowerCase().includes('stop') && o.stopPrice);

  return { tpOrder, slOrder };
};

interface BrokerManagementModalProps {
  isOpen: boolean;
  onClose: () => void;
  onOpenAccountStats?: () => void;
}

export const BrokerManagementModal: React.FC<BrokerManagementModalProps> = ({
  isOpen,
  onClose,
  onOpenAccountStats,
}) => {
  const {
    brokers,
    fetchBrokers,
    connectBroker,
    disconnectBroker,
    saveBrokerCredentials,
    selectBrokerAccount,
    setBrokerAutoConnect,
    setAccountEnabled,
  } = useBrokerStore();

  // Collapsible state per broker
  const [expandedBrokers, setExpandedBrokers] = useState<Set<string>>(new Set());

  // Credentials form state
  const [credentialsFor, setCredentialsFor] = useState<string | null>(null);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [savingCredentials, setSavingCredentials] = useState(false);

  // Add prop firm form state
  const [showAddForm, setShowAddForm] = useState(false);
  const [newFirmName, setNewFirmName] = useState('');
  const [newFirmUsername, setNewFirmUsername] = useState('');
  // Which tradovate-service instance drives this broker. 'us' is the home
  // instance and the default; 'india' routes it to the Mumbai instance so the
  // orders leave from an Indian IP.
  const [newFirmRegion, setNewFirmRegion] = useState<'us' | 'india'>('us');
  const [newFirmPassword, setNewFirmPassword] = useState('');
  const [addingFirm, setAddingFirm] = useState(false);

  // Action states
  const [connectingBroker, setConnectingBroker] = useState<string | null>(null);
  const [localError, setLocalError] = useState<string | null>(null);
  const [restartingService, setRestartingService] = useState(false);
  const [removingBroker, setRemovingBroker] = useState<string | null>(null);
  const [confirmRemove, setConfirmRemove] = useState<string | null>(null);

  const addToast = useToastStore((state) => state.addToast);
  const connectionStatus = useConnectionStatus();
  const isDisconnected = connectionStatus !== 'connected';

  // Restart tradovate-service via backend API
  const handleRestartService = async () => {
    setRestartingService(true);
    try {
      const response = await apiFetch(`${API_BASE_URL}/restart-tradovate-service`, {
        method: 'POST',
      });
      const data = await response.json();
      if (data.success) {
        addToast({
          type: 'success',
          message: 'Tradovate service restarting...',
          duration: 3000,
        });
      } else {
        addToast({
          type: 'error',
          message: data.error || 'Failed to restart service',
          duration: 5000,
        });
      }
    } catch (err) {
      addToast({
        type: 'error',
        message: 'Failed to restart tradovate-service',
        duration: 5000,
      });
    } finally {
      setRestartingService(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchBrokers();
      // Auto-expand connected brokers
      const connected = brokers.filter(b => b.status === 'connected').map(b => b.id);
      setExpandedBrokers(new Set(connected));
    }
  }, [isOpen, fetchBrokers]);

  // Track which brokers we've already auto-expanded (to avoid re-expanding after user collapses)
  const [autoExpandedBrokers, setAutoExpandedBrokers] = useState<Set<string>>(new Set());

  // Auto-expand newly connected brokers (only once per broker)
  useEffect(() => {
    brokers.forEach(b => {
      if (b.status === 'connected' && !autoExpandedBrokers.has(b.id)) {
        setExpandedBrokers(prev => new Set([...prev, b.id]));
        setAutoExpandedBrokers(prev => new Set([...prev, b.id]));
      }
      // Reset auto-expand tracking when broker disconnects
      if (b.status !== 'connected' && autoExpandedBrokers.has(b.id)) {
        setAutoExpandedBrokers(prev => {
          const next = new Set(prev);
          next.delete(b.id);
          return next;
        });
      }
    });
  }, [brokers, autoExpandedBrokers]);

  if (!isOpen) return null;

  const toggleExpanded = (brokerId: string) => {
    setExpandedBrokers(prev => {
      const next = new Set(prev);
      if (next.has(brokerId)) {
        next.delete(brokerId);
      } else {
        next.add(brokerId);
      }
      return next;
    });
  };

  const handleConnect = async (brokerId: string) => {
    setConnectingBroker(brokerId);
    setLocalError(null);
    const result = await connectBroker(brokerId);
    setConnectingBroker(null);
    if (!result.success) {
      setLocalError(result.error || 'Failed to connect');
    }
  };

  const handleDisconnect = async (brokerId: string) => {
    setConnectingBroker(brokerId);
    setLocalError(null);
    const result = await disconnectBroker(brokerId);
    setConnectingBroker(null);
    if (!result.success) {
      setLocalError(result.error || 'Failed to disconnect');
    }
  };

  const handleSaveCredentials = async () => {
    if (!credentialsFor || !username || !password) return;
    setSavingCredentials(true);
    setLocalError(null);
    const result = await saveBrokerCredentials(credentialsFor, username, password);
    setSavingCredentials(false);
    if (result.success) {
      setCredentialsFor(null);
      setUsername('');
      setPassword('');
    } else {
      setLocalError(result.error || 'Failed to save credentials');
    }
  };

  const handleAddPropFirm = async () => {
    if (!newFirmName.trim()) return;
    setAddingFirm(true);
    setLocalError(null);

    // Set up one-time listeners for response
    const handleAdded = (data: { success: boolean; brokerId: string; name: string }) => {
      setAddingFirm(false);
      setShowAddForm(false);
      setNewFirmName('');
      setNewFirmUsername('');
      setNewFirmPassword('');
      setNewFirmRegion('us');
      useToastStore.getState().addToast({
        type: 'success',
        message: `Added ${data.name}`,
        duration: 3000
      });
      wsService.off('broker:added', handleAdded);
      wsService.off('broker:error', handleError);
    };

    const handleError = (data: { brokerId: string; error: string }) => {
      // Only handle error if it's for our add request
      if (data.brokerId === newFirmName || data.brokerId === newFirmName.toLowerCase().replace(/[^a-z0-9]+/g, '-')) {
        setAddingFirm(false);
        setLocalError(data.error);
        wsService.off('broker:added', handleAdded);
        wsService.off('broker:error', handleError);
      }
    };

    wsService.on('broker:added', handleAdded);
    wsService.on('broker:error', handleError);

    // Emit the add request
    wsService.emit('broker:add', {
      name: newFirmName.trim(),
      username: newFirmUsername.trim(),
      password: newFirmPassword,
      region: newFirmRegion,
    });

    // Timeout after 10 seconds
    setTimeout(() => {
      if (addingFirm) {
        setAddingFirm(false);
        setLocalError('Request timed out');
        wsService.off('broker:added', handleAdded);
        wsService.off('broker:error', handleError);
      }
    }, 10000);
  };

  const handleRemoveBroker = (brokerId: string, brokerName: string) => {
    // First click shows confirm, second click removes
    if (confirmRemove !== brokerId) {
      setConfirmRemove(brokerId);
      // Reset confirm after 3 seconds if not clicked
      setTimeout(() => {
        setConfirmRemove((current) => current === brokerId ? null : current);
      }, 3000);
      return;
    }

    setRemovingBroker(brokerId);
    setConfirmRemove(null);
    setLocalError(null);

    // Set up one-time listeners for response
    const handleRemoved = (_data: { success: boolean; brokerId: string }) => {
      setRemovingBroker(null);
      useToastStore.getState().addToast({
        type: 'success',
        message: `Removed ${brokerName}`,
        duration: 3000
      });
      wsService.off('broker:removed', handleRemoved);
      wsService.off('broker:error', handleRemoveError);
    };

    const handleRemoveError = (data: { brokerId: string; error: string }) => {
      if (data.brokerId === brokerId) {
        setRemovingBroker(null);
        setLocalError(data.error);
        wsService.off('broker:removed', handleRemoved);
        wsService.off('broker:error', handleRemoveError);
      }
    };

    wsService.on('broker:removed', handleRemoved);
    wsService.on('broker:error', handleRemoveError);

    // Emit the remove request
    wsService.emit('broker:remove', { brokerId });

    // Timeout after 10 seconds
    setTimeout(() => {
      if (removingBroker === brokerId) {
        setRemovingBroker(null);
        setLocalError('Request timed out');
        wsService.off('broker:removed', handleRemoved);
        wsService.off('broker:error', handleRemoveError);
      }
    }, 10000);
  };

  const handleSelectAccount = (brokerId: string, accountId: number) => {
    selectBrokerAccount(brokerId, accountId);
  };

  // Find the globally selected account
  const getGlobalSelectedAccount = (): { brokerId: string; accountId: number } | null => {
    for (const broker of brokers) {
      if (broker.selectedAccountId && broker.status === 'connected') {
        return { brokerId: broker.id, accountId: broker.selectedAccountId };
      }
    }
    return null;
  };

  const globalSelected = getGlobalSelectedAccount();

  const getStatusIndicator = (status: BrokerState['status']) => {
    switch (status) {
      case 'connected':
        return <span className="status-dot connected" title="Connected" />;
      case 'connecting':
        return <span className="status-dot connecting" title="Connecting..." />;
      case 'error':
        return <span className="status-dot error" title="Error" />;
      default:
        return <span className="status-dot disconnected" title="Disconnected" />;
    }
  };

  const formatCurrency = (value: number | undefined) => {
    if (value === undefined || isNaN(value)) return '-';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatPnL = (value: number | undefined) => {
    if (value === undefined || isNaN(value)) return '-';
    const formatted = formatCurrency(Math.abs(value));
    if (value >= 0) {
      return <span className="pnl-positive">+{formatted}</span>;
    }
    return <span className="pnl-negative">-{formatted}</span>;
  };

  // Use portal to render modal at document body level, escaping any stacking context
  return createPortal(
    <div className="broker-modal-overlay" onClick={onClose}>
      <div className="broker-modal-v2" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="broker-modal-header">
          <h2>Broker Management</h2>
          <div className="broker-modal-header-actions">
            <button
              className="broker-modal-restart-btn"
              onClick={handleRestartService}
              disabled={restartingService}
              title="Restart Tradovate Service"
            >
              <RefreshCw size={16} className={restartingService ? 'spinning' : ''} />
              <span>{restartingService ? 'Restarting...' : 'Restart'}</span>
            </button>
            {onOpenAccountStats && (
              <button
                className="broker-modal-stats-btn"
                onClick={() => {
                  onClose();
                  onOpenAccountStats();
                }}
                title="Account Statistics"
              >
                <BarChart3 size={16} />
                <span>Stats</span>
              </button>
            )}
            <button className="broker-modal-close" onClick={onClose}>
              &times;
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="broker-modal-content">
          {isDisconnected && (
            <div className="broker-disconnected-warning">
              <WifiOff size={16} />
              <span>WebSocket disconnected - broker actions unavailable</span>
            </div>
          )}

          {localError && (
            <div className="broker-error">{localError}</div>
          )}

          {/* Add Prop Firm Button */}
          <button
            className="add-propfirm-btn"
            onClick={() => setShowAddForm(true)}
          >
            + Add Prop Firm
          </button>

          {/* Add Prop Firm Form */}
          {showAddForm && (
            <div className="add-propfirm-form">
              <div className="form-row">
                <input
                  type="text"
                  placeholder="Prop Firm Name"
                  value={newFirmName}
                  onChange={(e) => setNewFirmName(e.target.value)}
                  className="form-input"
                />
              </div>
              <div className="form-row">
                <input
                  type="text"
                  placeholder="Username"
                  value={newFirmUsername}
                  onChange={(e) => setNewFirmUsername(e.target.value)}
                  className="form-input"
                />
              </div>
              <div className="form-row">
                <input
                  type="password"
                  placeholder="Password"
                  value={newFirmPassword}
                  onChange={(e) => setNewFirmPassword(e.target.value)}
                  className="form-input"
                />
              </div>
              <div className="form-row">
                <label className="form-label" htmlFor="new-firm-region">
                  Region
                </label>
                <select
                  id="new-firm-region"
                  value={newFirmRegion}
                  onChange={(e) => setNewFirmRegion(e.target.value as 'us' | 'india')}
                  className="form-input"
                >
                  <option value="us">US (home)</option>
                  <option value="india">India (Mumbai)</option>
                </select>
              </div>
              <div className="form-actions">
                <button
                  className="btn-secondary"
                  onClick={() => {
                    setShowAddForm(false);
                    setNewFirmName('');
                    setNewFirmUsername('');
                    setNewFirmPassword('');
                    setNewFirmRegion('us');
                  }}
                >
                  Cancel
                </button>
                <button
                  className="btn-primary"
                  onClick={handleAddPropFirm}
                  disabled={addingFirm || !newFirmName.trim()}
                >
                  {addingFirm ? 'Adding...' : 'Add Prop Firm'}
                </button>
              </div>
            </div>
          )}

          {/* Broker List */}
          <div className="broker-list-v2">
            {brokers.map((broker) => {
              const isExpanded = expandedBrokers.has(broker.id);
              const isConnected = broker.status === 'connected';
              const isConnecting = connectingBroker === broker.id || broker.status === 'connecting';
              const accountBalances = broker.accountBalances || {};

              return (
                <div key={broker.id} className="broker-section">
                  {/* Broker Name Row */}
                  <div
                    className={`broker-name-row ${isConnected ? 'connected' : ''}`}
                    onClick={() => isConnected && toggleExpanded(broker.id)}
                  >
                    {isConnected && (
                      <span className={`expand-icon ${isExpanded ? 'expanded' : ''}`}>
                        {isExpanded ? '\u25BC' : '\u25B6'}
                      </span>
                    )}
                    <span className="broker-name">{broker.name}</span>
                    {broker.id === DEFAULT_BROKER_ID && (
                      <span className="default-badge">Default</span>
                    )}
                    {broker.isDemo && isConnected && (
                      <span className="demo-badge">DEMO</span>
                    )}
                  </div>

                  {/* Broker Actions Row */}
                  <div className="broker-actions-row">
                    <div className="broker-actions-left">
                      {getStatusIndicator(broker.status)}
                      <span className={`status-text ${broker.status}`}>
                        {broker.status === 'connected' ? 'Connected' :
                         broker.status === 'connecting' ? 'Connecting...' :
                         broker.status === 'error' ? 'Error' : 'Disconnected'}
                      </span>
                      {/* Auto-connect toggle for non-default brokers */}
                      {broker.id !== DEFAULT_BROKER_ID && (
                        <div className="auto-connect-toggle">
                          <label className="mini-toggle" title="Auto-connect on startup">
                            <input
                              type="checkbox"
                              checked={broker.autoConnect ?? false}
                              onChange={(e) => setBrokerAutoConnect(broker.id, e.target.checked)}
                            />
                            <span className="mini-slider"></span>
                          </label>
                          <span className="auto-connect-label">Auto</span>
                        </div>
                      )}
                    </div>
                    <div className="broker-actions-right">
                      {!isConnected && (
                        <button
                          className="btn-icon"
                          onClick={() => {
                            setCredentialsFor(broker.id);
                            setUsername('');
                            setPassword('');
                          }}
                          title="Set Credentials"
                        >
                          <span className="icon-key">&#x1F511;</span>
                        </button>
                      )}
                      {isConnected ? (
                        <button
                          className="btn-disconnect"
                          onClick={() => handleDisconnect(broker.id)}
                          disabled={isConnecting}
                        >
                          {isConnecting ? '...' : 'Disconnect'}
                        </button>
                      ) : (
                        <button
                          className="btn-connect"
                          onClick={() => handleConnect(broker.id)}
                          disabled={isConnecting || !broker.enabled}
                          title={!broker.enabled ? 'Broker is disabled' : undefined}
                        >
                          {isConnecting ? 'Connecting...' : 'Connect'}
                        </button>
                      )}
                      {/* Remove button (non-default brokers only) */}
                      {broker.id !== DEFAULT_BROKER_ID && !isConnected && (
                        <button
                          className={`btn-remove ${confirmRemove === broker.id ? 'confirm' : ''}`}
                          onClick={() => handleRemoveBroker(broker.id, broker.name)}
                          disabled={removingBroker === broker.id}
                          title={confirmRemove === broker.id ? 'Click again to confirm removal' : 'Remove broker'}
                        >
                          {removingBroker === broker.id ? '...' : confirmRemove === broker.id ? 'Confirm?' : '×'}
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Broker Error */}
                  {broker.error && (
                    <div className="broker-inline-error">{broker.error}</div>
                  )}

                  {/* Credentials Form (inline) */}
                  {credentialsFor === broker.id && (
                    <div className="credentials-inline">
                      <div className="cred-inputs">
                        <input
                          type="text"
                          placeholder="Username"
                          value={username}
                          onChange={(e) => setUsername(e.target.value)}
                          className="form-input small"
                        />
                        <input
                          type="password"
                          placeholder="Password"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          className="form-input small"
                        />
                      </div>
                      <div className="cred-actions">
                        <button
                          className="btn-secondary small"
                          onClick={() => {
                            setCredentialsFor(null);
                            setUsername('');
                            setPassword('');
                          }}
                        >
                          Cancel
                        </button>
                        <button
                          className="btn-primary small"
                          onClick={handleSaveCredentials}
                          disabled={savingCredentials || !username || !password}
                        >
                          {savingCredentials ? 'Saving...' : 'Save'}
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Accounts Table (expanded view) */}
                  {isConnected && isExpanded && broker.accounts.length > 0 && (
                    <div className="accounts-table">
                      <div className="accounts-table-header">
                        <div className="col-select"></div>
                        <div className="col-account">Account</div>
                        <div className="col-netliq">Net Liq</div>
                        <div className="col-pnl">P&L</div>
                        <div className="col-toggle">Enabled</div>
                      </div>
                      <div className="accounts-table-body">
                        {broker.accounts.map((account) => {
                          const isEnabled = account.enabled !== false;
                          const isSelected =
                            globalSelected?.brokerId === broker.id &&
                            globalSelected?.accountId === account.id;
                          const balance: BrokerBalance | undefined = accountBalances[account.id];

                          // Find positions for this account
                          const accountPositions = broker.positions.filter(
                            (p) => p.accountId === account.id && p.netPos !== 0
                          );

                          return (
                            <React.Fragment key={account.id}>
                              <div
                                className={`account-row ${isEnabled ? '' : 'disabled'} ${isSelected ? 'selected' : ''}`}
                                onClick={() => {
                                  if (isEnabled) {
                                    handleSelectAccount(broker.id, account.id);
                                  }
                                }}
                              >
                                <div className="col-select">
                                  <input
                                    type="radio"
                                    name="global-account"
                                    checked={isSelected}
                                    onChange={() => handleSelectAccount(broker.id, account.id)}
                                    disabled={!isEnabled}
                                    onClick={(e) => e.stopPropagation()}
                                  />
                                </div>
                                <div className="col-account">
                                  <span className="account-name" title={account.name}>
                                    {account.name}
                                  </span>
                                </div>
                                <div className="col-netliq">
                                  {formatCurrency(balance?.netLiq)}
                                </div>
                                <div className="col-pnl">
                                  {formatPnL(balance?.totalPnL)}
                                </div>
                                <div className="col-toggle" onClick={(e) => e.stopPropagation()}>
                                  <label className="mini-toggle">
                                    <input
                                      type="checkbox"
                                      checked={isEnabled}
                                      onChange={(e) => setAccountEnabled(broker.id, account.id, e.target.checked)}
                                    />
                                    <span className="mini-slider"></span>
                                  </label>
                                </div>
                              </div>
                              {/* Position details row */}
                              {accountPositions.length > 0 && (
                                <div className="position-detail-row">
                                  {accountPositions.map((pos) => {
                                    const { tpOrder, slOrder } = findOrdersForPosition(broker.orders, pos);
                                    const isLong = pos.netPos > 0;
                                    const qty = Math.abs(pos.netPos);
                                    const symbol = pos.symbol || `#${pos.contractId}`;

                                    return (
                                      <div key={pos.id} className="position-info">
                                        <span className={`pos-direction ${isLong ? 'long' : 'short'}`}>
                                          {isLong ? 'L' : 'S'}
                                        </span>
                                        <span className="pos-symbol">{symbol}</span>
                                        <span className="pos-qty">x{qty}</span>
                                        <span className="pos-entry">@{pos.netPrice.toFixed(2)}</span>
                                        {tpOrder?.price && (
                                          <span className="pos-tp" title="Take Profit">
                                            TP:{tpOrder.price.toFixed(2)}
                                          </span>
                                        )}
                                        {slOrder?.stopPrice && (
                                          <span className="pos-sl" title="Stop Loss">
                                            SL:{slOrder.stopPrice.toFixed(2)}
                                          </span>
                                        )}
                                        {!tpOrder?.price && !slOrder?.stopPrice && (
                                          <span className="pos-no-targets">No TP/SL</span>
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </React.Fragment>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* No accounts message */}
                  {isConnected && isExpanded && broker.accounts.length === 0 && (
                    <div className="no-accounts">No accounts available</div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Footer */}
        <div className="broker-modal-footer">
          <p className="broker-help-text">
            Select one account globally for trading. Toggle accounts to show/hide them.
          </p>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default BrokerManagementModal;
